import { useCallback, useEffect, useId, useRef, useState, type ReactNode, type Ref } from 'react';

export interface PopoverTriggerProps {
  'aria-expanded': boolean;
  'aria-controls': string;
  onClick: () => void;
  ref: Ref<HTMLButtonElement>;
}

export interface PopoverProps {
  /** Renders the trigger button; spread the props onto a <button>. */
  trigger: (props: PopoverTriggerProps, open: boolean) => ReactNode;
  children: (close: () => void) => ReactNode;
  label: string;
  align?: 'start' | 'end';
  className?: string;
}

/**
 * Disclosure-style popover: a button toggles a non-modal panel. Closes on Esc
 * (focus returns to the button), on outside click and when focus leaves.
 */
export function Popover({ trigger, children, label, align = 'end', className }: PopoverProps) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const close = useCallback((restoreFocus = true) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    const first = panel?.querySelector<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    (first ?? panel)?.focus({ preventScroll: true });

    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) close(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close(true);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  return (
    <div
      ref={rootRef}
      className={`popover${className ? ` ${className}` : ''}`}
      onBlur={(event) => {
        if (open && event.relatedTarget && !rootRef.current?.contains(event.relatedTarget as Node)) close(false);
      }}
    >
      {trigger(
        {
          'aria-expanded': open,
          'aria-controls': id,
          onClick: () => setOpen((value) => !value),
          ref: triggerRef,
        },
        open,
      )}
      {open && (
        <div
          ref={panelRef}
          id={id}
          role="dialog"
          aria-label={label}
          tabIndex={-1}
          className={`popover__panel glass glass--strong popover__panel--${align}`}
        >
          {children(() => close(true))}
        </div>
      )}
    </div>
  );
}
